import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';
import { useThemeStore } from '../stores/theme.store';
import { ThemeConfig } from '../config/theme';

interface ColorScale {
  '50': string;
  '100': string;
  '200': string;
  '300': string;
  '400': string;
  '500': string;
  '600': string;
  '700': string;
  '800': string;
  '900': string;
  '950': string;
}

interface TenantThemeResponse {
  id: string;
  tenantId: string;
  brandName: string;
  displayName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColors: ColorScale;
  accentColors: ColorScale;
  surfaceColors: ColorScale;
  fontFamily?: string;
  borderRadius?: string;
  layoutType: string;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
  status: string;
}

// Domínios que são considerados "domínio principal" da plataforma
// Nesses domínios, o tema padrão da plataforma é usado
const PLATFORM_DOMAINS = [
  'localhost',
  '127.0.0.1',
  'uniaobr.com',
  'www.uniaobr.com',
  'app.uniaobr.com',
  'hub.uniaobr.com',
];

/**
 * Verifica se o hostname atual é um domínio da plataforma (não de tenant)
 * Retorna true se for domínio principal, false se for subdomínio de tenant
 */
function isPlatformDomain(): boolean {
  const hostname = window.location.hostname.toLowerCase();
  
  // Verifica se é um dos domínios principais
  if (PLATFORM_DOMAINS.includes(hostname)) {
    return true;
  }
  
  // Verifica se é localhost com porta (ex: localhost:7001)
  if (hostname.startsWith('localhost') || hostname.startsWith('127.0.0.1')) {
    return true;
  }
  
  // Se for um subdomínio (ex: tenant1.uniaobr.com), não é domínio principal
  // Conta os pontos - se tiver mais de 1 ponto em .com, é subdomínio
  const parts = hostname.split('.');
  if (parts.length > 2 && !hostname.startsWith('www.') && !hostname.startsWith('app.') && !hostname.startsWith('hub.')) {
    // É um subdomínio de tenant (ex: tenant1.uniaobr.com)
    return false;
  }
  
  return true;
}

/**
 * Extrai o slug do tenant do hostname (se for subdomínio)
 * Ex: tenant1.uniaobr.com -> tenant1
 */
function getTenantSlugFromHostname(): string | null {
  const hostname = window.location.hostname.toLowerCase();
  const parts = hostname.split('.');
  
  // Se tiver mais de 2 partes e não for www/app/hub, é subdomínio de tenant
  if (parts.length > 2 && !['www', 'app', 'hub'].includes(parts[0])) {
    return parts[0];
  }
  
  return null;
}

// Converte ColorScale do backend (com keys string) para o formato do theme.ts
function convertColorScale(scale: ColorScale): { 50: string; 100: string; 200: string; 300: string; 400: string; 500: string; 600: string; 700: string; 800: string; 900: string; 950?: string } {
  return {
    50: scale['50'],
    100: scale['100'],
    200: scale['200'],
    300: scale['300'],
    400: scale['400'],
    500: scale['500'],
    600: scale['600'],
    700: scale['700'],
    800: scale['800'],
    900: scale['900'],
    950: scale['950'],
  };
}

// Converte o tema do backend para o formato do ThemeConfig
function convertToThemeConfig(theme: TenantThemeResponse): ThemeConfig {
  return {
    name: theme.brandName || 'Custom',
    colors: {
      primary: convertColorScale(theme.primaryColors),
      accent: convertColorScale(theme.accentColors),
      surface: convertColorScale(theme.surfaceColors),
      success: '34 197 94',
      warning: '234 179 8',
      error: '239 68 68',
      info: '59 130 246',
    },
    borderRadius: theme.borderRadius || '12px',
    sidebarWidth: '280px',
    headerHeight: '64px',
    logo: theme.logoUrl,
    favicon: theme.faviconUrl,
  };
}

/**
 * Hook que carrega e aplica o tema do tenant automaticamente
 * Deve ser usado no componente raiz da aplicação (App ou MainLayout)
 * 
 * REGRAS DE APLICAÇÃO DE TEMA:
 * 
 * 1. Super Admin: SEMPRE usa tema padrão da plataforma (cyber-cyan)
 * 2. Domínio Principal (localhost, uniaobr.com): Usa tema padrão da plataforma
 * 3. Subdomínio de Tenant (tenant1.uniaobr.com): Usa tema do tenant do subdomínio
 * 4. Usuário logado no seu tenant (mesmo domínio): Usa tema do seu tenant
 * 
 * Isso garante que:
 * - O site principal sempre mostra a identidade da plataforma
 * - Cada tenant tem sua própria identidade visual no seu subdomínio
 * - Super Admins sempre veem a interface padrão para editar temas sem confusão
 */
export function useTenantTheme() {
  const { user, isAuthenticated } = useAuthStore();
  const { setCustomTheme, setLayoutType, setTheme, setSuperAdminMode } = useThemeStore();
  const tenantId = user?.tenantId;
  const isSuperAdmin = user?.isSuperAdmin;

  // Verifica se estamos no domínio principal da plataforma
  const isOnPlatformDomain = useMemo(() => isPlatformDomain(), []);
  const tenantSlugFromUrl = useMemo(() => getTenantSlugFromHostname(), []);

  // Determina se devemos aplicar o tema do tenant
  // Regras:
  // 1. Super Admin NUNCA tem tema de tenant aplicado
  // 2. No domínio principal, usa tema padrão (não aplica tema de tenant)
  // 3. Em subdomínio de tenant, aplica tema daquele tenant
  // 4. Se não há subdomínio mas usuário está logado, aplica tema do tenant do usuário
  const shouldApplyTheme = useMemo(() => {
    // Super Admin nunca tem tema aplicado
    if (isSuperAdmin) {
      return false;
    }
    
    // Precisa estar autenticado e ter tenantId
    if (!isAuthenticated || !tenantId) {
      return false;
    }
    
    // Se estiver no domínio principal (localhost, uniaobr.com), NÃO aplica tema de tenant
    // O domínio principal sempre mostra o tema padrão da plataforma
    if (isOnPlatformDomain && !tenantSlugFromUrl) {
      console.log('[TenantTheme] On platform domain - using default theme');
      return false;
    }
    
    // Se estiver em subdomínio de tenant, aplica tema
    // Ou se o usuário está logado no seu próprio tenant
    return true;
  }, [isSuperAdmin, isAuthenticated, tenantId, isOnPlatformDomain, tenantSlugFromUrl]);

  // Debug logs
  console.log('[TenantTheme] Hook state:', {
    isAuthenticated,
    tenantId,
    isSuperAdmin,
    isOnPlatformDomain,
    tenantSlugFromUrl,
    shouldApplyTheme,
    userType: user?.type,
    hostname: window.location.hostname,
  });

  // Configurar o modo Super Admin no store para bloquear mudanças de tema
  useEffect(() => {
    if (isAuthenticated) {
      setSuperAdminMode(!!isSuperAdmin);
    }
  }, [isSuperAdmin, isAuthenticated, setSuperAdminMode]);

  // Para Super Admin OU domínio principal, garantir que o tema padrão seja aplicado
  useEffect(() => {
    const shouldUseDefaultTheme = isSuperAdmin || (isOnPlatformDomain && !tenantSlugFromUrl);
    
    if (shouldUseDefaultTheme && isAuthenticated) {
      console.log('[TenantTheme] Using default platform theme - reason:', 
        isSuperAdmin ? 'Super Admin' : 'Platform domain');
      // Sempre usa o tema padrão da plataforma
      setTheme('cyber-cyan');
      setLayoutType('SIDEBAR');
    }
  }, [isSuperAdmin, isAuthenticated, isOnPlatformDomain, tenantSlugFromUrl, setTheme, setLayoutType]);

  // Busca o tema do tenant (apenas se shouldApplyTheme for true)
  const { data: theme, isLoading, error } = useQuery({
    queryKey: ['tenant-theme', tenantId],
    queryFn: async () => {
      console.log('[TenantTheme] Fetching theme for tenant:', tenantId);
      // Usa o endpoint que não requer SuperAdmin
      const response = await api.get<TenantThemeResponse>(`/themes/my-theme`);
      console.log('[TenantTheme] Theme response:', response.data);
      return response.data;
    },
    enabled: shouldApplyTheme,
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: 1,
  });

  // Aplica o tema quando carregado (apenas quando shouldApplyTheme é true)
  useEffect(() => {
    // Verificação de segurança
    if (!shouldApplyTheme) {
      console.log('[TenantTheme] Skipping theme application - shouldApplyTheme is false');
      return;
    }

    console.log('[TenantTheme] useEffect triggered:', { theme: !!theme, shouldApplyTheme });
    
    if (theme) {
      const themeConfig = convertToThemeConfig(theme);
      console.log('[TenantTheme] Applying theme config:', themeConfig);
      
      // Atualiza o store (que também aplica o tema via applyTheme)
      setCustomTheme(themeConfig);
      
      // Atualiza o layout type se definido
      if (theme.layoutType) {
        setLayoutType(theme.layoutType as 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST');
      }

      // Atualiza favicon se definido
      if (theme.faviconUrl) {
        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (favicon) {
          favicon.href = theme.faviconUrl;
        }
      }

      console.log('[TenantTheme] Tema aplicado:', theme.brandName, '- Status:', theme.status);
    }
  }, [theme, shouldApplyTheme, setCustomTheme, setLayoutType]);

  return {
    theme,
    isLoading,
    error,
    isApplied: !!theme && shouldApplyTheme,
    isSuperAdmin,
    isOnPlatformDomain,
    tenantSlugFromUrl,
  };
}

export default useTenantTheme;
